import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { ListChecks, Loader2 } from 'lucide-react'

import { AiRequestError, getPlan } from 'services/aiService'
import { aiPlanReceived } from 'slices/aiSlice'

import { PlanCard } from '../components/PlanCard'

type Phase = { kind: 'loading' } | { kind: 'plan' } | { kind: 'missing' } | { kind: 'error'; message: string }

/**
 * One plan on a page of its own (MYD-648): where a plan proposed from the
 * Claude.ai connector is reviewed, edited and confirmed. The connector's
 * answer links here, so this is the only place that plan can run.
 *
 * It is the ordinary plan card, with its saving, progress, links and undo. The
 * server answers only the plan's owner, so anyone else, or an account without
 * AI, sees "not found" rather than learning the plan exists.
 */
export function PlanScreen({ load = getPlan }: { load?: typeof getPlan }) {
	const { id = '' } = useParams<{ id: string }>()
	const dispatch = useDispatch()
	const [phase, setPhase] = useState<Phase>({ kind: 'loading' })

	useEffect(() => {
		let live = true
		setPhase({ kind: 'loading' })
		load(id)
			.then(plan => {
				if (!live) return
				dispatch(aiPlanReceived(plan))
				setPhase({ kind: 'plan' })
			})
			.catch(error => {
				if (!live) return
				const status = error instanceof AiRequestError ? error.status : error?.response?.status
				if (status === 404 || status === 403 || status === 400) setPhase({ kind: 'missing' })
				else setPhase({ kind: 'error', message: "This plan couldn't be loaded just now. Try again in a moment." })
			})
		return () => {
			live = false
		}
	}, [id, load, dispatch])

	return (
		<div className="mx-auto max-w-2xl px-4 py-8">
			<h1 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900">
				<ListChecks className="h-5 w-5 text-primary" aria-hidden="true" />
				Plan from Ask MDT
			</h1>
			<p className="mb-4 text-sm text-gray-600">Review the steps, change anything that is wrong, then confirm. Nothing is changed until you do.</p>

			{phase.kind === 'loading' && (
				<p className="flex items-center gap-2 text-sm text-gray-600" role="status">
					<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading the plan…
				</p>
			)}

			{phase.kind === 'missing' && (
				<div role="alert" className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700">
					<p className="font-medium text-gray-900">Plan not found</p>
					<p className="mt-1">It may belong to another account, or no longer exist. Ask again to get a new one.</p>
					<Link to="/" className="mt-3 inline-block text-primary hover:underline">
						Go to Home
					</Link>
				</div>
			)}

			{phase.kind === 'error' && (
				<p role="alert" className="text-sm text-destructive">
					{phase.message}
				</p>
			)}

			{phase.kind === 'plan' && <PlanCard planId={id} />}
		</div>
	)
}

export default PlanScreen
